import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowRight,
  Eye,
  EyeOff,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { formatApiError } from "@/lib/errors";
import api from "@/lib/api";

/**
 * Password reset — step 2: complete the reset by setting a new password.
 * URL: /reset-password?token=xxx
 */
export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError(
        "This link is missing a reset token. Try requesting a new reset from the sign-in page."
      );
    }
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (pw1.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (pw1 !== pw2) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", {
        token,
        new_password: pw1,
      });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(formatApiError(err) || "Reset failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6 py-12">
        <div className="w-full max-w-sm" data-testid="reset-password-done">
          <div className="w-12 h-12 rounded-sm bg-emerald-100 flex items-center justify-center mb-4">
            <CheckCircle2 className="w-6 h-6 text-emerald-700" />
          </div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3 font-mono-data">
            All set
          </div>
          <h2 className="text-2xl font-display font-bold tracking-tight text-slate-900">
            Password updated
          </h2>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
            You'll be redirected to sign in with your new password in a moment…
          </p>
          <Link
            to="/login"
            className="mt-6 inline-block text-sm text-slate-900 hover:underline underline-offset-2"
          >
            Sign in now &rarr;
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6 py-12">
      <div className="w-full max-w-sm">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3 font-mono-data">
          Set new password
        </div>
        <h2 className="text-2xl font-display font-bold tracking-tight text-slate-900">
          Choose a new password
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Pick something only you know. At least 6 characters.
        </p>

        <form onSubmit={onSubmit} className="mt-8 space-y-5">
          <div>
            <Label
              htmlFor="new-password"
              className="text-xs uppercase tracking-wider"
            >
              New password
            </Label>
            <div className="relative mt-1.5">
              <Input
                id="new-password"
                type={showPw ? "text" : "password"}
                autoComplete="new-password"
                required
                minLength={6}
                value={pw1}
                onChange={(e) => setPw1(e.target.value)}
                className="rounded-sm pr-10"
                data-testid="reset-password-new"
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                aria-label={showPw ? "Hide password" : "Show password"}
                className="absolute inset-y-0 right-0 px-3 flex items-center text-slate-400 hover:text-slate-700"
                tabIndex={-1}
              >
                {showPw ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          <div>
            <Label
              htmlFor="confirm-password"
              className="text-xs uppercase tracking-wider"
            >
              Confirm password
            </Label>
            <Input
              id="confirm-password"
              type={showPw ? "text" : "password"}
              autoComplete="new-password"
              required
              minLength={6}
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              className="mt-1.5 rounded-sm"
              data-testid="reset-password-confirm"
            />
          </div>

          {error ? (
            <div
              className="text-sm text-destructive bg-red-50 border border-red-200 px-3 py-2 rounded-sm flex items-start gap-2"
              data-testid="reset-password-error"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}

          <Button
            type="submit"
            disabled={submitting || !token || !pw1 || !pw2}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-11 group"
            data-testid="reset-password-submit"
          >
            {submitting ? "Updating…" : "Update password"}
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
          </Button>
        </form>

        <div className="mt-10 text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data text-center">
          <Link
            to="/login"
            className="hover:text-slate-900 underline-offset-2 hover:underline"
          >
            Return to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
